// §3 — Settlement / Title. Agent block pulls from Encompass and is EDITABLE in
// the processor draft (Jennifer bug 5 — designer revision of the read-only
// prototype): corrections persist to the settlement jsonb and push back to
// Encompass (610/611/615/87) at the send-to-LO write moment. Read-only at all
// later stages. The doc-delivery email (closing section) and CC recipients
// (settlement jsonb) stay editable as before.
import { useState } from 'react';
import { EncField, EncFlag, Icon, ROField, SumField } from '../atoms';
import { useDocOrderCtx } from '../context';
import type { DocOrderSettlement } from '@/services/docOrderService';

function CcRecipients({ settlement }: { settlement: DocOrderSettlement | null }) {
  const { editable, patchSettlement } = useDocOrderCtx();
  const list = settlement?.ccRecipients ?? [];
  const [val, setVal] = useState('');
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  // Post-submission: an empty CC list is noise for downstream roles.
  if (!editable && list.length === 0) {
return null;
}
  const add = () => {
    const e = val.trim();
    if (!valid || list.some((x) => x.toLowerCase() === e.toLowerCase())) {
return;
}
    patchSettlement({ ccRecipients: [...list, e] });
    setVal('');
  };
  const remove = (e: string) => patchSettlement({ ccRecipients: list.filter((x) => x !== e) });

  return (
    <div className="cc-block">
      <span className="cc-label">
        <Icon name="users" size={13} /> CC on all closing communication
      </span>
      {(list.length > 0 || editable) && (
        <div className="cc-chips">
          {list.map((e) => (
            <span key={e} className="cc-chip">
              {e}
              {editable && (
                <button className="cc-chip-x" onClick={() => remove(e)} aria-label={`Remove ${e}`}>
                  <Icon name="x" size={12} />
                </button>
              )}
            </span>
          ))}
          {list.length === 0 && !editable && <span className="rail-note">None</span>}
          {editable && (
            <span className="cc-add">
              <input
                className="cc-add-inp"
                type="email"
                placeholder="add assistant / recipient email…"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    add();
                  }
                }}
              />
              <button className="cc-add-btn" disabled={!valid} onClick={add} aria-label="Add recipient">
                <Icon name="plus" size={14} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface SettlementProps {
  settlement: DocOrderSettlement | null;
  docEmail: string | null;
  stepNo: number;
}

export function SettlementSection({ settlement, docEmail, stepNo }: SettlementProps) {
  const { editable, patchClosing, patchSettlement } = useDocOrderCtx();
  const s = settlement;
  return (
    <section className="ws-section" id="sec-settlement">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no">{stepNo}</span>Settlement &amp; Title
          </h3>
          <EncFlag label="Synced from Encompass" />
        </div>
        <div className="field-grid compact">
          {editable ? (
            <>
              <EncField
                label="Settlement Agent"
                value={s?.agent ?? ''}
                span
                editable
                placeholder="settlement / title company"
                onChange={(v) => patchSettlement({ agent: String(v) })}
                encField={{ id: 610, desc: 'Settlement Agent' }}
              />
              <EncField
                label="Settlement Agent Contact"
                value={s?.contact ?? ''}
                editable
                placeholder="contact name"
                onChange={(v) => patchSettlement({ contact: String(v) })}
                encField={{ id: 611, desc: 'Settlement Agent Name' }}
              />
              <EncField
                label="Settlement Agent Phone"
                value={s?.phone ?? ''}
                editable
                mono
                format="phone"
                placeholder="818-555-0134"
                onChange={(v) => patchSettlement({ phone: String(v) })}
                encField={{ id: 615, desc: 'Settlement Agent Phone' }}
              />
              <EncField
                label="Settlement Agent Email"
                value={s?.email ?? ''}
                editable
                mono
                type="email"
                placeholder="escrow@title.com"
                onChange={(v) => patchSettlement({ email: String(v) })}
                encField={{ id: 87, desc: 'Settlement Agent Email' }}
              />
            </>
          ) : (
            <>
              <ROField label="Settlement Agent" value={s?.agent} span encField={{ id: 610, desc: 'Settlement Agent' }} />
              <ROField label="Settlement Agent Contact" value={s?.contact} encField={{ id: 611, desc: 'Settlement Agent Name' }} />
              <ROField label="Settlement Agent Phone" value={s?.phone} mono encField={{ id: 615, desc: 'Settlement Agent Phone' }} />
              <ROField label="Settlement Agent Email" value={s?.email} mono encField={{ id: 87, desc: 'Settlement Agent Email' }} />
            </>
          )}
          {editable ? (
            <div className="field doc-email-field">
              <span className="field-label">
                <Icon name="mail" size={14} /> Email address for Loan Docs <span className="req">*</span>
              </span>
              <input
                className="inp"
                type="email"
                placeholder="where should final docs be delivered?"
                value={docEmail ?? ''}
                onChange={(e) => patchClosing({ doc_email: e.target.value })}
              />
              <span className="rail-note doc-email-hint">Defaults to the settlement agent email — change if docs go elsewhere.</span>
            </div>
          ) : (
            <SumField
              label="Email address for Loan Docs"
              value={docEmail}
              mono
              sub={docEmail && docEmail !== s?.email ? 'Differs from the settlement agent email — docs deliver here.' : null}
            />
          )}
          <div className="field span-2">
            <CcRecipients settlement={s} />
          </div>
        </div>
      </div>
    </section>
  );
}
